import React, { useState, useEffect } from 'react';
import {
  Users,
  Building2,
  MapPin,
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Sprout,
  Layers
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

/**
 * Admin Dashboard Component
 * 
 * Main dashboard providing system overview, statistics, and key metrics
 * for the Farm Monitor admin panel.
 */
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, new: 0 },
    farms: { total: 0, active: 0, new: 0 },
    plots: { total: 0, active: 0, new: 0 },
    beds: { total: 0, active: 0, new: 0 },
    submissions: { total: 0, pending: 0, resolved: 0, critical: 0 },
  });
  const [chartData, setChartData] = useState({
    monthly: [],
    statusDistribution: [],
    categoryDistribution: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  console.log('Dashboard component mounting/rendering...');

  useEffect(() => {
    console.log('Dashboard useEffect triggered');
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Starting dashboard data load...');
      console.log('Auth token:', apiService.getAuthToken() ? 'Present' : 'Missing');

      // Load dashboard statistics
      const response = await apiService.getDashboardStats();

      console.log('Dashboard stats response:', response);
      
      if (response && response.success && response.data && response.data.stats) {
        console.log('Setting stats:', response.data.stats);
        console.log('Setting charts:', response.data.charts);
        console.log('Beds data from API:', response.data.stats.beds);

        // Ensure all required properties exist with defaults
        const statsData = {
          users: response.data.stats.users || { total: 0, active: 0, new: 0 },
          farms: response.data.stats.farms || { total: 0, active: 0, new: 0 },
          plots: response.data.stats.plots || { total: 0, active: 0, new: 0 },
          beds: response.data.stats.beds || { total: 0, active: 0, new: 0 },
          submissions: response.data.stats.submissions || { total: 0, pending: 0, resolved: 0, critical: 0 }
        };

        console.log('Processed statsData:', statsData);
        console.log('Beds in statsData:', statsData.beds);

        const chartsData = {
          monthly: response.data.charts?.monthly || [],
          statusDistribution: response.data.charts?.statusDistribution || [],
          categoryDistribution: response.data.charts?.categoryDistribution || []
        };

        setStats(statsData);
        setChartData(chartsData);
        console.log('Successfully loaded dashboard data from API');
        console.log('Stats state after setting:', statsData);
      } else {
        console.log('Dashboard API failed or returned invalid data, falling back...');
        console.log('Response:', response);
        setError('Dashboard API returned invalid data, using fallback data');
        await loadFallbackData();
      }
    } catch (error) {
      console.error('Dashboard loading error:', error);
      setError(`Dashboard API error: ${error.message || 'Unknown error'}`);
      // Try fallback approach
      await loadFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const loadFallbackData = async () => {
    try {
      console.log('Loading fallback data...');
      const [usersRes, farmsRes, plotsRes, bedsRes, submissionsRes] = await Promise.allSettled([
        apiService.getUsers({ limit: 1000 }),
        apiService.getFarms({ limit: 1000 }),
        apiService.getPlots({ limit: 1000 }),
        apiService.getBeds({ limit: 1000 }),
        apiService.getSubmissions({ limit: 1000 })
      ]);

      console.log('Fallback responses:', { usersRes: usersRes.status, farmsRes: farmsRes.status, plotsRes: plotsRes.status, bedsRes: bedsRes.status, submissionsRes: submissionsRes.status });

      // Process users data
      const users = usersRes.status === 'fulfilled' && usersRes.value.success ? usersRes.value.data.users || [] : [];
      const activeUsers = users.filter(user => {
        const lastLogin = new Date(user.last_login_at);
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return lastLogin > thirtyDaysAgo;
      });
      const newUsers = users.filter(user => {
        const created = new Date(user.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process farms data
      const farms = farmsRes.status === 'fulfilled' && farmsRes.value.success ? farmsRes.value.data.farms || [] : [];
      const newFarms = farms.filter(farm => {
        const created = new Date(farm.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process plots data
      const plots = plotsRes.status === 'fulfilled' && plotsRes.value.success ? plotsRes.value.data.plots || [] : [];
      const newPlots = plots.filter(plot => {
        const created = new Date(plot.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process beds data
      const beds = bedsRes.status === 'fulfilled' && bedsRes.value.success ? bedsRes.value.data.beds || [] : [];
      console.log('Fallback beds data:', beds);
      const newBeds = beds.filter(bed => {
        const created = new Date(bed.created_at);
        const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return created > sevenDaysAgo;
      });

      // Process submissions data
      const submissions = submissionsRes.status === 'fulfilled' && submissionsRes.value.success ? submissionsRes.value.data.submissions || [] : [];
      const pendingSubmissions = submissions.filter(s => s.status === 'open' || s.status === 'in_progress');
      const resolvedSubmissions = submissions.filter(s => s.status === 'resolved' || s.status === 'closed');
      const criticalSubmissions = submissions.filter(s => s.severity === 'critical');

      // Set fallback stats
      const fallbackStats = {
        users: {
          total: users.length,
          active: activeUsers.length,
          new: newUsers.length
        },
        farms: {
          total: farms.length,
          active: farms.length,
          new: newFarms.length
        },
        plots: {
          total: plots.length,
          active: plots.length,
          new: newPlots.length
        },
        beds: {
          total: beds.length,
          active: beds.length,
          new: newBeds.length
        },
        submissions: {
          total: submissions.length,
          pending: pendingSubmissions.length,
          resolved: resolvedSubmissions.length,
          critical: criticalSubmissions.length
        },
      };

      console.log('Fallback stats being set:', fallbackStats);
      console.log('Fallback beds stats:', fallbackStats.beds);
      setStats(fallbackStats);

      // Generate simple chart data
      const statusDistribution = [
        { name: 'Open', value: submissions.filter(s => s.status === 'open').length, color: '#ef4444' },
        { name: 'In Progress', value: submissions.filter(s => s.status === 'in_progress').length, color: '#f59e0b' },
        { name: 'Resolved', value: submissions.filter(s => s.status === 'resolved').length, color: '#10b981' },
        { name: 'Closed', value: submissions.filter(s => s.status === 'closed').length, color: '#6b7280' },
      ];

      const categoryDistribution = [
        { name: 'Farm', value: submissions.filter(s => s.category === 'Farm').length },
        { name: 'Plot', value: submissions.filter(s => s.category === 'Plot').length },
        { name: 'Plant', value: submissions.filter(s => s.category === 'Plant').length },
        { name: 'Other', value: submissions.filter(s => !s.category || !['Farm', 'Plot', 'Plant'].includes(s.category)).length },
      ];

      setChartData({
        monthly: [], // Would need more complex processing for monthly data
        statusDistribution,
        categoryDistribution,
      });

      // Clear error since fallback data loaded successfully
      setError('');
      console.log('Fallback data loaded successfully, error cleared');

    } catch (fallbackError) {
      console.error('Fallback data loading failed:', fallbackError);
      setError('Failed to load dashboard data');
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'primary', onClick }) => (
    <div
      className={`bg-white rounded-lg shadow p-6 ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow duration-200' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center">
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        <div className="ml-4 flex-1">
          <p className="text-sm font-medium text-secondary-600">{title}</p>
          <p className="text-2xl font-bold text-secondary-900">{value.toLocaleString()}</p>
          {subtitle && <p className="text-sm text-secondary-500">{subtitle}</p>}
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${trend.positive ? '' : 'rotate-180'}`} />
            {trend.value}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-secondary-200 rounded-lg"></div>
                <div className="ml-4 flex-1">
                  <div className="h-4 bg-secondary-200 rounded w-24 mb-2"></div>
                  <div className="h-6 bg-secondary-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Dashboard</h1>
          <p className="text-secondary-600">Overview of your farm monitoring system</p>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${user?.role === 'farms_owner' ? 'lg:grid-cols-4' : 'lg:grid-cols-5'}`}>
        <StatCard
          title="Total Farms"
          value={stats.farms.total}
          subtitle={`${stats.farms.active} active`}
          icon={Building2}
          trend={stats.farms.new > 0 ? { positive: true, value: `+${stats.farms.new} this week` } : null}
          color="green"
          onClick={() => navigate('/farms')}
        />
        <StatCard
          title="Total Plots"
          value={stats.plots.total}
          subtitle={`${stats.plots.active} active`}
          icon={MapPin}
          trend={stats.plots.new > 0 ? { positive: true, value: `+${stats.plots.new} this week` } : null}
          color="indigo"
          onClick={() => navigate('/plots')}
        />
        <StatCard
          title="Total Beds"
          value={stats.beds.total}
          subtitle={`${stats.beds.active} active`}
          icon={Layers}
          trend={stats.beds.new > 0 ? { positive: true, value: `+${stats.beds.new} this week` } : null}
          color="purple"
          onClick={() => navigate('/beds')}
        />
        <StatCard
          title="Total Submissions"
          value={stats.submissions.total}
          subtitle={`${stats.submissions.pending} pending`}
          icon={FileText}
          trend={stats.submissions.critical > 0 ? { positive: false, value: `${stats.submissions.critical} critical` } : null}
          color="orange"
          onClick={() => navigate('/submissions')}
        />
        {/* Hide Total Users card for farms_owner */}
        {user?.role !== 'farms_owner' && (
          <StatCard
            title="Total Users"
            value={stats.users.total}
            subtitle={`${stats.users.active} active`}
            icon={Users}
            trend={stats.users.new > 0 ? { positive: true, value: `+${stats.users.new} this week` } : null}
            color="blue"
            onClick={() => navigate('/users')}
          />
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Critical Issues */}
        {stats.submissions.critical > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
              <h3 className="text-lg font-semibold text-red-900">Critical Issues</h3>
            </div>
            <p className="text-red-700 mb-4">
              {stats.submissions.critical} critical issues need immediate attention
            </p>
            <button className="btn btn-danger">View Critical Issues</button>
          </div>
        )}

        {/* Recent Activity - Hidden for farms_owner
        {user?.role !== 'farms_owner' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                <div>
                  <p className="text-sm font-medium">New farm registered</p>
                  <p className="text-xs text-secondary-500">2 hours ago</p>
                </div>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-yellow-500 mr-3" />
                <div>
                  <p className="text-sm font-medium">Issue reported</p>
                  <p className="text-xs text-secondary-500">4 hours ago</p>
                </div>
              </div>
              <div className="flex items-center">
                <Users className="w-5 h-5 text-blue-500 mr-3" />
                <div>
                  <p className="text-sm font-medium">New user registered</p>
                  <p className="text-xs text-secondary-500">1 day ago</p>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* System Health */}
         {/* <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">System Health</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600">Database</span>
              <span className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Online
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600">API Service</span>
              <span className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Healthy
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary-600">Storage</span>
              <span className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Available
              </span>
            </div>
          </div>
        </div> */}
      </div>

      {/* Charts */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
        {/* Submission Status Distribution
        {chartData.statusDistribution.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Submission Status</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.statusDistribution}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {chartData.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )} */}

        {/* Category Distribution
        {chartData.categoryDistribution.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Issues by Category</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.categoryDistribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#339f68" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )} */}
      {/* </div> */}
    </div>
  );
};

export default Dashboard;